import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, map, pluck, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {cache} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {InvoiceFilterSortOptions, InvoiceFilterSortPipe} from '../invoice-filter-sort.pipe';

@Component({
  selector: 'invoices-page',
  templateUrl: './invoices-page.component.html',
  styleUrls: ['../styles/paginator.scss', '../styles/search-sort.scss', './invoices-page.component.scss']
})
export class InvoicesPageComponent implements OnInit, OnDestroy {

  purchaseOrdersTab = environment.flags.invoicePurchaseOrdersTab;

  private invoicesSub: Subscription;
  private transitionSub: Function;

  defaultFilterSortOptions: DefaultInvoiceFilterSortOptions = {
    statement: {
      sort: 'date-desc',
      view: 'statement'
    },
    invoices: {
      sort: 'date-desc',
      view: 'invoices'
    }
  };

  statementSortOptions = new Map<string, [string, {}]>([
    ['date-desc', ['invoices.sort.date-desc', {}]],
    ['date-asc', ['invoices.sort.date-asc', {}]]
  ]);

  invoiceSortOptions = new Map<string, [string, {}]>([
    ['date-desc', ['invoices.sort.date-desc', {}]],
    ['date-asc', ['invoices.sort.date-asc', {}]]
  ]);

  countOptions = new Map<number, [string, {}]>([
    [10, ['pagination.count-option', {number: 10}]],
    [25, ['pagination.count-option', {number: 25}]],
    [50, ['pagination.count-option', {number: 50}]],
    [100, ['pagination.count-option', {number: 100}]]
  ]);

  allInvoices$: Observable<{invoices: Invoice[], totalBalance: number, currency: string}>;
  invoices$: Observable<Invoice[]>;
  lowVal$: Observable<number>;
  highVal$: Observable<number>;
  total$: Observable<number>;
  loading$ = new BehaviorSubject(true);
  currentPage$ = new BehaviorSubject(1);
  pageSize$ = new BehaviorSubject(10);
  reload$ = new Subject<void>();
  totalBilled$: Observable<number>;
  amountDue$: Observable<number>;
  currency$: Observable<string>;
  filterSortOptions$ = new BehaviorSubject<InvoiceFilterSortOptions>(this.defaultFilterSortOptions.statement);

  constructor(private route: ActivatedRoute,
              private Analytics: AnalyticsService,
              private Api: ClientApiService,
              private filterSortPipe: InvoiceFilterSortPipe,
              private Navigation: NavigationService) {
  }

  ngOnInit() {
    this.initializeFilterSortForView();
    this.setupInvoiceSource();
    this.setupInvoicesToDisplay();
    this.setupPagination();

    this.amountDue$ = this.allInvoices$.pipe(pluck('totalBalance'));
    this.currency$ = this.allInvoices$.pipe(pluck('currency'));
    this.totalBilled$ = this.invoices$.pipe(map(invoices => this.getTotalBilled(invoices)));
    this.invoicesSub = this.invoices$.subscribe();
    this.reload$.next();
  }

  initializeFilterSortForView() {
    this.route.paramMap.subscribe(params => {
      this.updateFilterSort(this.defaultFilterSortOptions[params.get('view')]);
      this.currentPage$.next(1);
    });
  }

  updateFilterSort(options: Partial<InvoiceFilterSortOptions>) {
    this.filterSortOptions$.next({...this.filterSortOptions$.getValue(), ...options});
  }

  setupInvoicesToDisplay() {
    this.invoices$ = combineLatest(
      this.allInvoices$,
      this.filterSortOptions$,
      (allInvoices, filterSortOptions) => this.filterSortPipe.transform(allInvoices.invoices, filterSortOptions)
    );
  }

  setupInvoiceSource() {
    this.allInvoices$ = this.reload$
      .pipe(
        tap(() => this.loading$.next(true)),
        switchMap(() => this.Api.getStatement()),
        tap(() => this.loading$.next(false)),
        tap(() => this.currentPage$.next(1)),
        catchError((err: HttpError) => {
          console.error('Failed to load invoices.', err);
          this.Navigation.handleError(err);
          return of(null);
        }),
        cache()
      );
  }

  setupPagination() {
    this.lowVal$ = combineLatest(
      this.currentPage$,
      this.pageSize$,
      (currentPage, pageSize) => ((currentPage - 1) * pageSize) + 1
    );

    this.highVal$ = combineLatest(
      this.currentPage$,
      this.pageSize$,
      this.invoices$,
      (currentPage, pageSize, invoices) => Math.min(invoices.length, currentPage * pageSize)
    );

    this.total$ = this.invoices$.pipe(map(invoices => invoices.length));
  }

  ngOnDestroy(): void {
    if (this.transitionSub) this.transitionSub();
    if (this.invoicesSub) this.invoicesSub.unsubscribe();
  }

  getTotalBilled(invoices: Invoice[]): number {
    let total = 0.0;
    const statementInvoices: Invoice[] = this.filterSortPipe.transform(invoices, this.defaultFilterSortOptions.statement);

    for (const invoice of statementInvoices) {
      total += invoice.billed;
    }

    return total;
  }

  setCount(count: number): void {
    this.pageSize$.next(count);
    this.currentPage$.next(1);
  }

  setSort(sort) {
    this.updateFilterSort({sort});
    this.Analytics.trackEvent('Invoices: Sort set to ' + sort);
  }

}

interface DefaultInvoiceFilterSortOptions {
  statement: InvoiceFilterSortOptions;
  invoices: InvoiceFilterSortOptions;
}
